import {
  convertImageSizeForAutomation,
  convertImageStyleForAutomation,
  ImageSize,
  ImageStyle,
} from '@/constants/tools/ai-image-generator'
import projectApi from '@/redux/api/projectApi'
import { useCallback, useMemo, useState } from 'react'
import { useMountedState } from 'react-use'

const ART = {
  IMAGE: 'IMAGE',
  VIDEO: 'VIDEO',
} as const

type GeneratedImage = {
  input: {
    topic: string
    imageStyle: ImageStyle
    imageSize: ImageSize
  }
  image: {
    url: string
    width: number
    height: number
  }
}

type GeneratedVideo = {
  input: {
    topic: string
  }
  video: {
    videoUrl: string
    thumbnailUrl: string
    width: number
    height: number
    /**
     * unit: seconds
     */
    duration: number
  }
}

type UseGenerateArt = {
  (param: {
    type: typeof ART.IMAGE
    input: {
      topic: string
      imageStyle: ImageStyle
      imageSize: ImageSize
    }
    initialGenerated?: GeneratedImage
  }): {
    generate: () => Promise<GeneratedImage | void>
    isGenerating: boolean
    error: Error | null
    isError: boolean
    generated: GeneratedImage | null
    isGenerated: boolean
  }
  (param: {
    type: typeof ART.VIDEO
    input: {
      topic: string
    }
    initialGenerated?: GeneratedVideo
  }): {
    generate: () => Promise<GeneratedVideo | void>
    isGenerating: boolean
    error: Error | null
    isError: boolean
    generated: GeneratedVideo | null
    isGenerated: boolean
  }
}

/**
 * FIXME: 타입 관련된 것은 이어서 다시 보자
 */
// @ts-expect-error
const useGenerateArt: UseGenerateArt = ({ type, input, initialGenerated }) => {
  const [isGenerating, setIsGenerating] = useState(false)
  const [error, setError] = useState<Error | null>(null)
  const [generated, setGenerated] = useState<
    GeneratedImage | GeneratedVideo | null
  >(() => {
    if (!initialGenerated) {
      return null
    }

    return initialGenerated
  })

  const isGenerated = useMemo(() => !!generated, [generated])
  const isError = useMemo(() => !!error, [error])

  const [requestGenerate] = projectApi.useGenerateImageMutation()

  const [getGeneratingJob] = projectApi.useLazyGetGenerateJobIdQuery({
    refetchOnFocus: false,
    refetchOnReconnect: false,
  })

  const isMounted = useMountedState()

  const generate = useCallback(async () => {
    if (isGenerating) {
      return
    }

    const trimmedTopic = input.topic.trim()

    if (!trimmedTopic) {
      return
    }

    try {
      /**
       * 이미지/비디오 생성중 상태로 변경
       */
      setIsGenerating(true)

      /**
       * 상태 초기화
       */
      setGenerated(null)
      setError(null)

      const options: {
        type?: 'image' | 'video'
        script: string
        style?: string
        size?: string
      } = {
        script: trimmedTopic,
      }

      if (type === ART.IMAGE) {
        const convertedImageStyle = convertImageStyleForAutomation(
          input.imageStyle,
        )
        const convertedImageSize = convertImageSizeForAutomation(
          input.imageSize,
        )
        options.type = 'image'
        options.style = convertedImageStyle
        options.size = convertedImageSize
      } else if (type === ART.VIDEO) {
        options.type = 'video'
        options.style = 'photo' // 고정
        options.size = 'landscape' // 고정
      }

      /**
       * 이미지/비디오 생성 작업 요청
       */
      const responseRequestedGenerate: {
        success: boolean
        data: {
          automationJobId: string
        }
      } = await requestGenerate(options).unwrap()

      if (!responseRequestedGenerate.success) {
        throw new Error('failed to generate')
      }

      const { automationJobId } = responseRequestedGenerate.data

      /**
       * 생성 작업의 진행 상태를 수시로 확인
       */
      while (true) {
        await new Promise((resolve) => setTimeout(resolve, 1000))

        /**
         * 언마운트 되는 경우 에러 및 루프 중단
         */
        if (!isMounted()) {
          throw new Error('component is unmounted')
        }

        const responseGetGeneratingJob: {
          success: boolean
          data: {
            result: {
              directResult: {
                images: {
                  url: string
                  width: number
                  height: number
                }[]
                videos: {
                  url: string
                  videoUrl: string
                  width: number
                  height: number
                  /**
                   * unit: seconds
                   */
                  duration: number
                }[]
              }
            }
            status: 'working' | 'finished' | 'error'
          }
        } = await getGeneratingJob(automationJobId).unwrap()

        if (!responseGetGeneratingJob.success) {
          throw new Error('failed to get generating image job')
        }

        if (responseGetGeneratingJob.data.status === 'error') {
          throw new Error('failed to generate image')
        }

        if (responseGetGeneratingJob.data.status === 'finished') {
          if (type === ART.IMAGE) {
            const generatedImage =
              responseGetGeneratingJob.data.result.directResult.images[0]

            const generatedResult = {
              input: {
                topic: trimmedTopic,
                imageStyle: input.imageStyle,
                imageSize: input.imageSize,
              },
              image: {
                url: generatedImage.url,
                width: generatedImage.width,
                height: generatedImage.height,
              },
            }

            setGenerated(generatedResult)

            return generatedResult
          } else if (type === ART.VIDEO) {
            const generatedVideo =
              responseGetGeneratingJob.data.result.directResult.videos[0]

            const generatedResult = {
              input: {
                topic: trimmedTopic,
              },
              video: {
                videoUrl: generatedVideo.videoUrl,
                thumbnailUrl: generatedVideo.url,
                width: generatedVideo.width,
                height: generatedVideo.height,
                duration: generatedVideo.duration,
              },
            }

            setGenerated(generatedResult)

            return generatedResult
          }
        }
      }
    } catch (e) {
      setError(e as Error)
      throw e
    } finally {
      setIsGenerating(false)
    }
  }, [isGenerating, input, type, requestGenerate, getGeneratingJob, isMounted])

  return {
    generate,
    isGenerating,
    error,
    isError,
    generated,
    isGenerated,
  }
}

export const useGenerateImage = ({
  topic,
  imageStyle,
  imageSize,
  initialGenerated,
}: {
  topic: string
  imageStyle: ImageStyle
  imageSize: ImageSize
  initialGenerated?: GeneratedImage
}) => {
  const trimmedTopic = useMemo(() => topic.trim(), [topic])

  const { generate, isGenerating, generated, isGenerated, isError, error } =
    useGenerateArt({
      type: ART.IMAGE,
      input: {
        topic: trimmedTopic,
        imageStyle,
        imageSize,
      },
      initialGenerated,
    })

  return {
    generate,
    isGenerating,
    error,
    isError,
    generated,
    isGenerated,
  }
}

export const useGenerateVideo = ({
  topic,
  initialGenerated,
}: {
  topic: string
  initialGenerated?: GeneratedVideo
}) => {
  const trimmedTopic = useMemo(() => topic.trim(), [topic])

  const { generate, isGenerating, generated, isGenerated, isError, error } =
    useGenerateArt({
      type: ART.VIDEO,
      input: {
        topic: trimmedTopic,
      },
      initialGenerated,
    })

  return {
    generate,
    isGenerating,
    error,
    isError,
    generated,
    isGenerated,
  }
}
